import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { Container } from 'components/layout'
import { CustomSelectOption } from 'types/index'
import AnalyticsService from 'services/AnalyticsLite/AnalyticsService'
import { TopicConstraint } from 'services/ContentfulService/constraints'
import { Breadcrumbs } from 'components/elements/Breadcrumbs'
import { Article } from 'services/ContentfulService/models/articles'
import { Topic } from 'services/ContentfulService/models/topics'
import { Dropdown } from 'components/elements'
import { useTopics } from 'services/ContentfulService/hooks/topics'
import { PaginationFragment } from 'experiences/learn/articles/ArticleDirectory/PaginationFragment'
import Link from 'next/link'
import { ApplySection } from '../../common/applySection'
import { Breadcrumb } from 'shared/layout/CustomHead/Schemas/breadcrumbSchema'
import styled from '@emotion/styled'
import { COLORS, PAGE_BREAKPOINTS } from 'parameters/index'
import { LearnOverride } from 'experiences/learn/common/shared.styles'
import { TopicsList } from 'experiences/learn/articles/ArticleDirectory/TopicsExplore'
import { FullRow, GridContainer, MasonryGridItem } from 'experiences/learn/articles/ArticleGrid/styles'

interface Props {
	initialArticles: Article[]
	topic?: Topic
	initialPage?: number
	dividers?: JSX.Element[]
}

const defaultSelectOption = {
	label: 'All Articles',
	value: ''
}

export const ArticleDirectoryPage: React.FC<React.PropsWithChildren<Props>> = ({
	initialArticles,
	topic,
	initialPage,
	dividers = []
}): JSX.Element => {
	const constraints = topic ? [TopicConstraint(topic.id)] : []
	const router = useRouter()
	const [pageCount, setPageCount] = useState(1)
	const [morePostsAvailable, setMorePostsAvailable] = useState(true)
	const [allTopics] = useTopics({ constraints: [], count: 100 })
	const pages: JSX.Element[] = []
	for (let i = 0; i < pageCount; i++) {
		pages.push(
			<PaginationFragment
				key={`article-directory-${i}`}
				initialArticles={i == 0 ? initialArticles : undefined}
				constraints={constraints}
				offset={(initialPage || 0) + i}
				onNoMorePosts={() => setMorePostsAvailable(false)}
				WrapperComponent={MasonryGridItem}
				divider={dividers[i] && <FullRow key={`divider-${i}`}>{dividers[i]}</FullRow>}
			/>
		)
	}

	const availableTopics = allTopics ? allTopics : ([topic].filter(t => t != undefined) as Topic[])
	const selectOptions: CustomSelectOption[] = [
		defaultSelectOption,
		...availableTopics
			.sort((a, b) => a.title.localeCompare(b.title))
			.map(t => {
				return { label: t.name, value: t.slug }
			})
	]

	const handleOnChange = (option: CustomSelectOption): void => {
		AnalyticsService.track('Topic Filter', { category: 'Learn', label: option.label })
		option.value === ''
			? router.push({ pathname: '/learn/articles' })
			: router.push({ pathname: '/learn/topics/[topic]/articles', query: { topic: option.value } })
	}

	const breadcrumbs: Breadcrumb[] = [
		{ name: 'Learn', url: '/learn' },
		...(topic ? [{ name: topic.name, url: topic.relativePath }] : [])
	]

	const paginationLink = (direction: 'next' | 'prev') => {
		const destinationCount = direction === 'next' ? pageCount + 1 : pageCount - 1
		const text = direction === 'next' ? 'See Older Posts' : 'See Newer Posts'
		const url = { pathname: router.pathname, query: { page: destinationCount, topic: topic?.slug } }
		return (
			<OlderPostsText>
				<Link
					shallow={true}
					href={url}
					onClick={event => {
						if (direction === 'next') {
							event.preventDefault()
							setPageCount(destinationCount)
						} else {
							router.push(url)
						}
					}}
				>
					{text}
				</Link>
			</OlderPostsText>
		)
	}

	return (
		<>
			<Container>
				<Breadcrumbs breadcrumbs={breadcrumbs} />
				<ArticlesTop>
					<ArticlesTitle>{topic && topic.name + ' '}Articles</ArticlesTitle>
					<LearnOverride>
						<Dropdown options={selectOptions} initialValue={topic ? topic.slug : ''} onChange={handleOnChange} />
					</LearnOverride>
				</ArticlesTop>
				{(initialPage || 0) > 0 && paginationLink('prev')}
				<GridContainer>{pages}</GridContainer>
				{morePostsAvailable ? paginationLink('next') : <TopicsList topics={availableTopics} />}
			</Container>
			<ApplySection />
		</>
	)
}

const ArticlesTitle = styled.h1`
	margin: 32px 0;
	min-height: 32px;
	@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		margin-top: 16px;
		margin-bottom: 16px;
	}
`

const ArticlesTop = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		flex-direction: column;
		align-items: flex-start;
		margin-bottom: 40px;
	}
`

const OlderPostsText = styled.div`
	color: ${COLORS.KSU_PURPLE};
	height: 80px;
	display: flex;
	justify-content: center;
	align-items: center;

	span {
		cursor: pointer;
	}
`
